'use client';

import React, { useEffect, useState } from 'react';
import { getAuth, signInWithPopup, GoogleAuthProvider, OAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { app } from '../config/firebaseConfig';

const Login = ({ nextUrl: propNextUrl }) => {
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check if the user exists in the local database.
   * Returns true/false.
   */
  const checkUserCreated = async (token) => {
    try {
      // Use a cache-busting timestamp to avoid browser caching
      const timestamp = new Date().getTime();
      const checkUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/check_user_created?_t=${timestamp}`;
      
      const response = await fetch(checkUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store, no-cache, must-revalidate'
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Check user error: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();  // { exists: boolean }
      return data.exists;
    } catch (err) {
      throw err;
    }
  };

  /**
   * Call the endpoint to accept terms.
   */
  const acceptTerms = async (token) => {
    try {
      const termsURL = `${process.env.NEXT_PUBLIC_BACKEND_URL}/terms/accept`;
      const response = await fetch(termsURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error syncing terms:', response.status, errorText);
      } else {
        console.log('Terms accepted endpoint called successfully.');
      }
    } catch (err) {
      console.error('Error accepting terms:', err);
    }
  };

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('Login component - onAuthStateChanged:', currentUser ? currentUser.uid : 'No user');

      if (currentUser) {
        // Use the nextUrl from props if provided, otherwise get from URL
        const nextURL = propNextUrl || new URLSearchParams(window.location.search).get('next') || '/';

        try {
          const token = await currentUser.getIdToken();
          
          // Only perform verification on fresh logins (marked by auth_action)
          const isNewLogin = localStorage.getItem('auth_action') === 'login';
          const alreadyVerified = sessionStorage.getItem('backend_verified') === 'true';
          
          if (isNewLogin && !alreadyVerified) {
            console.log('Fresh login detected, checking user in database...');
            setChecking(true);
            
            try {
              // Check if user exists in backend
              let userCreated = await checkUserCreated(token);
              let retryCount = 0;
              const maxRetries = 5; // Reduced number of retries
              
              // Wait for user to be created in backend if needed
              while (!userCreated && retryCount < maxRetries) {
                console.log(`User not yet created in database, waiting... (Attempt ${retryCount + 1}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, 3000));
                userCreated = await checkUserCreated(token);
                retryCount++;
              }
              
              if (userCreated) {
                console.log('User found in the local database!');
                
                // Mark as verified to prevent future checks
                sessionStorage.setItem('backend_verified', 'true');
                
                // Clear login action flag
                localStorage.removeItem('auth_action');
                
                // Accept terms
                await acceptTerms(token);
              } else {
                console.error('Failed to find user in database after maximum retries');
                setError('User creation in database failed. Please try again.');
                localStorage.removeItem('auth_action');
              }
            } catch (err) {
              console.error('Error verifying user:', err);
              setError(`Verification error: ${err.message}`);
              localStorage.removeItem('auth_action');
            } finally {
              setChecking(false);
            }
          } else {
            console.log('Not a fresh login or already verified, skipping verification');
            if (isNewLogin) {
              localStorage.removeItem('auth_action');
            }
          }

          // Always redirect to the next URL
          console.log('Redirecting to:', nextURL);
          window.location.href = nextURL;
        } catch (err) {
          console.error('Error after login:', err);
          setError(`Error after login: ${err.message}`);
          localStorage.removeItem('auth_action');
        } finally {
          setLoading(false);
        }
      } else {
        // If no authorized user
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [propNextUrl]);

  const handleLogin = (provider) => {
    setLoading(true);
    setError(null);
    
    // Set login action flag to identify fresh logins
    localStorage.setItem('auth_action', 'login');
    
    // Clear verification status for new login
    sessionStorage.removeItem('backend_verified');
    
    const auth = getAuth(app);
    signInWithPopup(auth, provider)
      .then(() => {
        console.log('Sign-in popup successful. Auth state change will handle verification.');
      })
      .catch((err) => {
        console.error('Error during sign-in:', err);
        setError(err.message);
        setLoading(false);
        localStorage.removeItem('auth_action');
      });
  };

  const handleGoogleLogin = () => {
    const provider = new GoogleAuthProvider();
    handleLogin(provider);
  };

  const handleAppleLogin = () => {
    const provider = new OAuthProvider('apple.com');
    handleLogin(provider);
  };

  /**
   * Render different states:
   * - Loading Firebase (loading)
   * - Checking user in local database (checking)
   * - Normal state when buttons are clickable
   */
  if (loading) {
    if (checking) {
      return <div>Checking if user exists in the database...</div>;
    }
    return <div>Loading...</div>;
  }

  return (
    <div className="centered_container flex flex-col items-center justify-center min-h-[80vh] py-8">
      <div className="mb-12">
        <h2 style={{ fontWeight: 'bold' }}>davooda</h2>
      </div>
      <h1 className="login-h1 text-4xl font-bold mb-8">Sign In</h1>
      {error && <div className="error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">Error: {error}</div>}
      <button 
        className="google-sign-in-button flex items-center justify-center bg-white hover:bg-gray-50 text-gray-800 font-medium py-3 px-4 border border-gray-300 rounded-full w-72 mb-4"
        onClick={handleGoogleLogin}
      >
        <img src="/images/google_icon.svg" alt="Google sign-in" className="google-icon w-5 h-5 mr-3" />
        Continue with Google
      </button>
      <button 
        className="apple-sign-in-button flex items-center justify-center bg-white hover:bg-gray-50 text-gray-800 font-medium py-3 px-4 border border-gray-300 rounded-full w-72" 
        onClick={handleAppleLogin}
      >
        <img src="/images/apple_icon.svg" alt="Apple sign-in" className="apple-icon w-5 h-5 mr-3" />
        Continue with Apple
      </button>
    </div>
  );
};

export default Login;